import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import {
  Button,
  Card,
  Col,
  Container,
  Modal,
  Row,
  Table,
} from 'react-bootstrap';
import html2canvas from 'html2canvas-pro';
import { jsPDF } from 'jspdf';
import { getBarColor } from '../../util/util';
import type { AssessmentReport } from '../../hooks/useGetAssessmentReport';
import ScoreIcon from '../common/ScoreIcon';
import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { getPreambleText } from './util';
import { getLocalizedString } from '../../util/getLocalizedString';
import logo from '../../assets/LogoHorizontal.png';
import { captureSupabaseError } from '../../lib/sentry';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  type ChartOptions,
} from 'chart.js';
import '../../styles/print.scss';

interface AssessmentReportDisplayNewProps {
  report: AssessmentReport;
  isPublished?: boolean;
}

const isPrinting =
  new URLSearchParams(window.location.search).get('print') === 'true';
const PDF_RENDER_SCALE = 1.25;
const PDF_IMAGE_QUALITY = 0.72;

export const AssessmentReportNewDisplay = ({
  report,
}: AssessmentReportDisplayNewProps) => {
  const { meta, sections, created } = report;
  const [generatingLink, setGeneratingLink] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const reportIntroRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    if (!meta?.supplier_name || !meta.template_name) return;

    document.title = `${meta.supplier_name} - ${meta.template_name} report | SupplyPassport App`;
  }, [meta?.supplier_name, meta?.template_name]);

  if (!meta) {
    return (
      <Container className="mt-4">
        <p>Report metadata is missing. Unable to display report.</p>
      </Container>
    );
  }

  const barColor = getBarColor(meta.overall_percentage);

  const handleDownloadClick = async (fileValue: string) => {
    if (!fileValue) return;

    try {
      setGeneratingLink(true);

      const { data, error } = await supabase.storage
        .from('assessment-answers')
        .createSignedUrl(fileValue, 60 * 60); // valid for 1 hour

      if (error) {
        captureSupabaseError(error, { op: 'storage.createSignedUrl' });
        console.error('Error creating signed URL:', error);
        setToastMessage(
          'There was an error generating the download link. Please try again later.',
        );
        setShowToast(true);
        return;
      }

      window.open(data.signedUrl, '_blank');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handlePdfDownload = async () => {
    if (generatingPdf) return;

    try {
      setGeneratingPdf(true);
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const availableWidth = pageWidth - margin * 2;
      const availableHeight = pageHeight - margin * 2;

      const renderPage = async (
        element: HTMLDivElement,
        addNewPage = false,
      ) => {
        const canvas = await html2canvas(element, {
          scale: PDF_RENDER_SCALE,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          windowWidth: element.scrollWidth,
          windowHeight: element.scrollHeight,
        });

        const imageData = canvas.toDataURL('image/jpeg', PDF_IMAGE_QUALITY);
        const scale = Math.min(
          availableWidth / canvas.width,
          availableHeight / canvas.height,
        );
        const imageWidth = canvas.width * scale;
        const imageHeight = canvas.height * scale;
        const x = (pageWidth - imageWidth) / 2;

        if (addNewPage) {
          pdf.addPage();
        }

        pdf.addImage(
          imageData,
          'JPEG',
          x,
          margin,
          imageWidth,
          imageHeight,
          undefined,
          'MEDIUM',
        );
      };

      if (reportIntroRef.current) {
        await renderPage(reportIntroRef.current);
      }

      for (const sectionRef of sectionRefs.current) {
        if (!sectionRef) continue;
        await renderPage(sectionRef, true);
      }

      pdf.save(
        `${meta.supplier_name || 'assessment-report'}-${new Date(created || Date.now()).toISOString().slice(0, 10)}.pdf`,
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
      setToastMessage(
        'There was an error generating the PDF. Please try again later.',
      );
      setShowToast(true);
    } finally {
      setGeneratingPdf(false);
    }
  };

  ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

  const barChartData = {
    labels: [
      meta.supplier_name,
      'Similar suppliers Avg',
      'Industry Avg',
      'All suppliers Avg',
    ],
    datasets: [
      {
        label: 'ESG Score',
        data: [
          meta.overall_percentage,
          meta.size_average_percentage ?? 64,
          meta.industry_average_percentage ?? 72,
          meta.others_average ?? 65,
        ],
        backgroundColor: [
          'rgba(42, 36, 90, 1)',
          '#E5E7EB',
          '#E5E7EB',
          '#E5E7EB',
        ],
      },
    ],
  };

  const chartOptions: ChartOptions<'bar'> = {
    indexAxis: 'y', // Horizontal bars
    responsive: true,
    animation: false, // Disable animations for print
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          font: {
            size: 14,
            family: 'Lexend, Helvetica, sans-serif',
            weight: 600,
          },
        },
      },
      x: {
        ticks: {
          font: {
            size: 14,
            family: 'Lexend, Helvetica, sans-serif',
            weight: 600,
          },
        },
      },
    },
  };

  return (
    <>
      <div className="bg-dark fixed-top" style={{ zIndex: 1000 }}>
        <Container>
          {!isPrinting && (
            <div className="d-flex justify-content-end my-2">
              <Button
                variant="light"
                onClick={handlePdfDownload}
                disabled={generatingPdf}
              >
                <i className="bi bi-file-earmark-pdf me-2" />
                {generatingPdf ? 'Generating PDF...' : 'Download PDF'}
              </Button>
            </div>
          )}
        </Container>
      </div>
      <Container className="mt-5 pt-4">
        <div>
          <div ref={reportIntroRef}>
            <div className="mx-3">
              <div>
                <img
                  src={logo}
                  alt="SupplyPassport Logo"
                  height="60"
                  className="mb-2"
                />
              </div>
              <h3 className="p-2 bg-primary text-white">
                {meta.template_name}
              </h3>
              <h3>{meta.supplier_name}</h3>
              <h6>
                {created &&
                  `Assessment date: ${new Date(created).toLocaleDateString()}`}
              </h6>
              <p>{getPreambleText(meta.tags, meta.supplier_name)}</p>
            </div>
            <Card.Body>
              <Container fluid>
                {/* Header */}
                <Row>
                  <Col xs={12} md={3}>
                    {/* Overall Score */}
                    <div className="d-flex flex-column align-items-center p-2">
                      <div style={{ maxWidth: '300px', maxHeight: '300px' }}>
                        <CircularProgressbar
                          value={Math.round(meta.overall_percentage)}
                          text={`${Math.round(meta.overall_percentage)}%`}
                          styles={buildStyles({
                            pathColor: barColor,
                            trailColor: '#E5E7EB',
                            textColor: '#111827',
                            textSize: '16px',
                            strokeLinecap: 'butt',
                          })}
                        />
                      </div>
                      <h5 className="mt-3">Overall Score</h5>
                    </div>
                  </Col>

                  <Col xs={12} md={9}>
                    <div className="d-flex flex-column p-2 h-100 justify-content-center">
                      <p className="text-center">
                        How does this supplier compare to others?
                      </p>
                      <div
                        style={{ maxHeight: '300px' }}
                        className="d-flex justify-content-center"
                      >
                        <Bar data={barChartData} options={chartOptions} />
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row className="mt-4">
                  <Col>
                    {/* Sections Table */}
                    <div className="p-2 mt-4">
                      <h3>Section Scores</h3>
                      <hr />
                    </div>
                  </Col>
                </Row>
              </Container>
            </Card.Body>
          </div>
          <Card.Body>
            <Container fluid>
              <Row className="mt-4">
                <Col>
                  <div className="p-2 mt-4">
                    <div className="">
                      {sections.map((section, index) => (
                        <div
                          key={section.id}
                          className="mb-2"
                          ref={(element) => {
                            sectionRefs.current[index] = element;
                          }}
                        >
                          <div>
                            <h2 className="p-1 bg-primary-subtle">
                              {section.title}
                            </h2>
                          </div>
                          <div>
                            <Row>
                              <Col
                                xs={12}
                                md={3}
                                className="d-flex flex-column align-items-center"
                              >
                                <div
                                  style={{
                                    maxWidth: '200px',
                                    maxHeight: '200px',
                                  }}
                                  className="d-flex flex-column align-items-center p-2"
                                >
                                  <CircularProgressbar
                                    value={Math.round(section.percentage)}
                                    text={`${Math.round(section.percentage)}%`}
                                    styles={buildStyles({
                                      pathColor: barColor,
                                      trailColor: '#E5E7EB',
                                      textColor: '#111827',
                                      textSize: '16px',
                                      strokeLinecap: 'butt',
                                    })}
                                  />
                                  <h5 className="mt-3">Section Score</h5>
                                </div>
                              </Col>
                              <Col
                                xs={12}
                                md={9}
                                className="d-flex align-items-center pt-3"
                              >
                                {section.description && (
                                  <div
                                    className="ms-4"
                                    dangerouslySetInnerHTML={{
                                      __html: getLocalizedString(
                                        section.description,
                                      ),
                                    }}
                                  ></div>
                                )}
                              </Col>
                            </Row>
                            {section.groups.length > 0 && (
                              <>
                                <h6 className="mt-3 bg-warning-subtle p-2">
                                  Group Scores
                                </h6>
                                <Table>
                                  <tbody>
                                    {section.groups.map((group, gIdx) => (
                                      <tr key={gIdx}>
                                        <td>{group.title}</td>
                                        <td style={{ width: '120px' }}>
                                          <ScoreIcon value={group.percentage} />
                                          <span className="ms-2">
                                            {group.percentage}%
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </>
                            )}
                            {section.exposed_answers.length > 0 && (
                              <>
                                <h6 className="mt-3 bg-success-subtle p-2">
                                  Key Answers
                                </h6>
                                <Table bordered>
                                  <tbody>
                                    {section.exposed_answers.map(
                                      (answer, aIdx) => (
                                        <tr key={aIdx}>
                                          <td>{answer.question_title}</td>
                                          <td>{String(answer.value)}</td>
                                        </tr>
                                      ),
                                    )}
                                  </tbody>
                                </Table>
                              </>
                            )}
                            {section.file_answers.length > 0 && (
                              <>
                                <h6 className="mt-3 bg-info-subtle p-2">
                                  Attached Files
                                </h6>
                                <Table>
                                  <tbody>
                                    {section.file_answers.map((file, fIdx) =>
                                      file.file_value ? (
                                        <tr key={fIdx}>
                                          <td>{file.question_title}</td>
                                          <td className="d-flex justify-content-end">
                                            {isPrinting || generatingPdf ? (
                                              <a
                                                href={file.file_value}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                              >
                                                View File
                                              </a>
                                            ) : (
                                              <Button
                                                size="sm"
                                                variant="primary"
                                                data-testid="download-file-btn"
                                                onClick={() => {
                                                  handleDownloadClick(
                                                    file.file_value,
                                                  );
                                                }}
                                                disabled={generatingLink}
                                              >
                                                <i className="bi bi-cloud-download me-2" />{' '}
                                                Download
                                              </Button>
                                            )}
                                          </td>
                                        </tr>
                                      ) : null,
                                    )}
                                  </tbody>
                                </Table>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          </Card.Body>
          <Modal show={showToast} onHide={() => setShowToast(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Notification</Modal.Title>
            </Modal.Header>
            <Modal.Body>{toastMessage}</Modal.Body>
            <Modal.Footer>
              <Button variant="primary" onClick={() => setShowToast(false)}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>
          <p className="text-center">
            This report was generated by SupplyPassport. For more information,
            visit{' '}
            <a href="https://www.supplypassport.co">www.supplypassport.co</a>
          </p>
        </div>
      </Container>
      <div id="pdf-render-complete" style={{ display: 'none' }} />
    </>
  );
};
